import { Col } from 'react-bootstrap';
import React, { useContext, useState } from 'react';
import { Checkbox } from '@progress/kendo-react-inputs';

import Popup from '../../popup-window';
import { DetailPlayer } from '../detail-player';
import { TaskContentAnswerEntity } from '../../../types';
import { roundingDecimalNumber } from '../../../utils/number';
import { ManualScoring } from '../../../types/manual-scoring';
import { GameDocumentContext } from '../../../contexts/game-document';
import { Task, TaskContentAnswer } from '../../../types/entities/task';
import { GetResourceValue } from '../../../utils/game-document/resources';
import { TaskContent } from '../../../types/game-document/entities/task-content';
import { GetTaskContentById } from '../../../utils/game-document/assets/task-contents';

interface TaskCardContentProps {
  index?: number;
  isEdit?: boolean;
  playerCode?: string;
  playerName?: string;
  selectedTaskContent?: TaskContent;
  taskContentAnswer?: TaskContentAnswer;
  selectedDownload: string[];
  onSubmitScore?: (e: ManualScoring) => void;
  onAddRemoveSelectedDownload: (value: string, checked: boolean) => void;
}

export const TaskContentCard = ({
  isEdit = true,
  ...props
}: TaskCardContentProps) => {
  const [gameDocument] = useContext(GameDocumentContext);
  const [selectedAnswer, setSelectedAnswer] = useState<
    ManualScoring | undefined
  >();

  const onSelectEdit = (
    playerCode?: string,
    taskId?: string,
    formId?: string,
    answer?: string,
    isScored?: boolean,
    score?: number
  ) => {
    let answerSelected: ManualScoring = {
      playerName: props?.playerName,
      gameCode: gameDocument?.gameCode,
      playerCode: playerCode!,
      taskId: taskId,
      formId: formId,
      answer: answer,
      isScored: isScored,
      score: score
    };

    setSelectedAnswer(answerSelected);
  };

  const isSelectedDownload = (value: string) =>
    Boolean(props.selectedDownload.find((e) => e === value));

  const getAnswerList = (
    taskContentId: string,
    formId: string,
    answers: TaskContentAnswerEntity
  ): React.ReactNode[] => {
    const answerList: React.ReactNode[] = [];

    if (!taskContentId) {
      return answerList;
    }

    const taskContent = GetTaskContentById(
      gameDocument!.gameDocument,
      taskContentId!
    );

    const form = taskContent?.forms?.find((x) => x.id === formId);

    const floatingCheckbox = (value: string) => (
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: '5px'
        }}>
        <Checkbox
          defaultChecked={isSelectedDownload(value)}
          onChange={(e) => props.onAddRemoveSelectedDownload(value, e.value)}
        />
      </div>
    );

    if (form) {
      if (form.type === 'checkbox' || form.type === 'radio') {
        answers?.answers
          ?.filter((x) => x.value)
          ?.forEach((answer, index) => {
            const description = GetResourceValue(
              gameDocument?.gameDocument!,
              answer.answerResId,
              ''
            );
            answerList.push(
              <label key={index} className={'text-break'}>
                {description}
              </label>
            );
          });
      } else if (form.type === 'image') {
        answers?.answers?.forEach((answer, index) => {
          answerList.push(
            <div
              key={`image-wrapper-${index}-${answer?.value}`}
              className={'d-flex align-items-center position-relative'}>
              <img
                key={answer.value}
                src={answer.value}
                className={'img-fluid'}
                alt="Answer Image"
              />
              {floatingCheckbox(answer.value)}
            </div>
          );
        });
      } else if (form.type === 'file') {
        answers?.answers?.forEach((answer, index) => {
          answerList.push(
            <a key={`file-link-${index}-${answer?.value}`} href={answer?.value} target="_blank">
              Open
            </a>
          );
        });
      } else if (form.type === 'video') {
        answers?.answers?.forEach((answer, index) => {
          answerList.push(
            <div
              key={`video-wrapper-${index}-${answer?.value}`}
              className={`position-relative`}>
              <video
                key={answer?.value}
                src={answer?.value}
                preload={'metadata'}
                crossOrigin={'anonymous'}
                className={`video h-100 w-100 position-relative`}
                controls
                playsInline>
                <source src={answer?.value} type={'video/mp4'} />
              </video>
              {floatingCheckbox(answer.value)}
            </div>
          );
        });
      } else {
        answers?.answers?.forEach((answer, index) => {
          answerList.push(
            <label className={'text-break'}>{answer?.value}</label>
          );
        });
      }
    }
    return answerList;
  };

  const checkIsManualScoring = (taskContentId: string, formId: string) => {
    let taskContent = GetTaskContentById(
      gameDocument?.gameDocument,
      taskContentId!
    );
    let form = taskContent?.forms?.find((x) => x.id === formId);
    return form?.isManualScoring ? 'M' : 'A';
  };

  return (
    <Col md={4} className={'p-1'} key={props.index}>
      <div className={'d-flex flex-column'}>
        <span className={'fw-bold'}>{props?.playerName}</span>
        <div className={'card p-3'}>
          <div className={'mb-3 d-flex justify-content-between'}>
            <span>
              Task :{' '}
              {GetResourceValue(
                gameDocument!.gameDocument!,
                props.selectedTaskContent?.titleResId ?? '',
                ''
              )}
            </span>
            <span
              className={'text-success'}
              style={{
                textAlign: 'right'
              }}>
              On-Demand Task
            </span>
          </div>
          <div className={'d-flex w-full justify-content-between'}>
            <div className={'d-flex flex-column'}>
              <small className={'text-muted'}>Score</small>
              <div className={'d-flex'}>
                <h1 className={'text-success mr-3'}>
                  {checkIsManualScoring(
                    props!.selectedTaskContent?.id ?? '',
                    props!.taskContentAnswer!.formId!
                  )}
                </h1>
                <h1 className={'text-dark'}>
                  {roundingDecimalNumber(
                    props!.taskContentAnswer!.score! ?? 0,
                    2
                  )}
                </h1>
              </div>
            </div>

            <div>
              <a
                href="#"
                className={'cursor-pointer'}
                onClick={() =>
                  onSelectEdit(
                    props!.playerCode,
                    props!.selectedTaskContent?.id,
                    props!.taskContentAnswer!.formId!,
                    JSON.stringify(props?.taskContentAnswer),
                    checkIsManualScoring(
                      props!.selectedTaskContent?.id ?? '',
                      props!.taskContentAnswer!.formId!
                    ) === 'A'
                      ? true
                      : false || (props?.taskContentAnswer?.score ?? 0) > 0,
                    props?.taskContentAnswer?.score
                  )
                }>
                {isEdit ? 'Edit' : 'View'}
              </a>
            </div>
          </div>
          <div className={'d-flex w-full'}>
            <div className={'flex-grow-1'}>
              <div className={'d-flex flex-column'}>
                <small className={'text-muted mb-2'}>Answer</small>
                {props?.selectedTaskContent &&
                  getAnswerList(
                    props.selectedTaskContent.id,
                    props.taskContentAnswer!.formId!,
                    props.taskContentAnswer!
                  ).map((item, index) => (
                    <React.Fragment key={index}>{item}</React.Fragment>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedAnswer && (
        <Popup
          handleClose={() => setSelectedAnswer(undefined)}
          width={'600'}
          className={
            'box assessment-export d-flex flex-column position-relative'
          }>
          <DetailPlayer
            key={`detail-${selectedAnswer.playerCode}-${selectedAnswer.taskId}-${selectedAnswer.formId}`}
            type={'OnDemandTask'}
            dataPlayer={selectedAnswer}
            onSubmitScore={(e) => {
              if (props.onSubmitScore) {
                props.onSubmitScore(e);
                setSelectedAnswer(undefined);
              }
            }}></DetailPlayer>
        </Popup>
      )}
    </Col>
  );
};
